import type { WovarCustomer } from "@commercekit/api";
import { useLocale, useLocalePath } from "@commercekit/remix/index";
import { fallbackMoney, formatMoneyWithCurrency } from "@commercekit/shared";
import { useNavigation } from "@remix-run/react";
import {
  getSwitcherDisplayPrice,
  type ProductSwitcher,
  type ProductSwitcherAttribute,
  type ProductSwitcherOption,
} from "~/lib/productSwitcher";
import WvLink from "../atoms/WvLink";

const WvProductSwitcher = ({
  switcher,
  customer,
}: {
  switcher: ProductSwitcher;
  customer?: WovarCustomer;
}) => {
  const { localeFullName } = useLocale();
  const localePath = useLocalePath();
  const notB2B = !customer || customer.type === "B2C";
  const navigation = useNavigation();
  const pendingPath = navigation.state === "loading" ? navigation.location.pathname : null;

  const renderContent = (attribute: ProductSwitcherAttribute, option: ProductSwitcherOption) => {
    if (attribute.display === "image") {
      return (
        <>
          {option.image ? (
            <img
              src={option.image.url}
              alt=""
              width={100}
              height={60}
              decoding="async"
              className="pdp-switcher__image"
            />
          ) : (
            <span className="pdp-switcher__image pdp-switcher__image--empty" />
          )}
          <span className="pdp-switcher__name">{option.value}</span>
        </>
      );
    }
    const price = option.prices ? getSwitcherDisplayPrice(option.prices, notB2B) : null;
    return (
      <>
        <span className="pdp-switcher__value">{option.value}</span>
        {price ? (
          <span className="pdp-switcher__price" data-price={price.value}>
            {formatMoneyWithCurrency(localeFullName, price.currencyCode ? price : fallbackMoney())}
            {switcher.priceSuffix ? ` ${switcher.priceSuffix}` : ""}
          </span>
        ) : null}
      </>
    );
  };

  const renderOption = (attribute: ProductSwitcherAttribute, option: ProductSwitcherOption) => {
    const className = `pdp-switcher__option pdp-switcher__option--${attribute.display}`;
    const content = renderContent(attribute, option);
    if (option.active) {
      return (
        <span key={option.value} className={`${className} is-active`} aria-current="page">
          {content}
        </span>
      );
    }
    const to = localePath(`/${option.slug}`);
    const isPending = pendingPath === to;
    return (
      <WvLink
        key={option.value}
        to={to}
        className={`${className}${isPending ? " is-pending" : ""}`}
      >
        {content}
        {isPending && (
          <i className="pdp-switcher__spinner zmdi-hc-spin" data-icon-after={"\uf3ec"} />
        )}
      </WvLink>
    );
  };

  return (
    <div
      className={`pdp-switcher${pendingPath ? " is-loading" : ""}`}
      data-switcher={switcher.id}
      aria-busy={!!pendingPath}
    >
      {switcher.attributes.map((attribute) => (
        <div
          key={attribute.key}
          className={`pdp-switcher__section pdp-switcher__section--${attribute.display}`}
        >
          <div className="pdp-switcher__title">
            {attribute.label}: <strong>{attribute.selectedLabel}</strong>
          </div>
          <div className="pdp-switcher__options">
            {attribute.options.map((option) => renderOption(attribute, option))}
          </div>
        </div>
      ))}
      <div className="pdp-switcher__divider" />
    </div>
  );
};

export default WvProductSwitcher;
